import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  Plus,
  Loader2,
  MessageSquare,
  Check,
  X,
  Calendar,
  User,
  Phone,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const normalizePhoneKey = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 9 ? digits.slice(-9) : digits;
};

const matchesSearch = (req, term) => {
  if (!term) return true;
  const q = term.toLowerCase();
  return [
    req.clientName,
    req.detectedName,
    req.clientPhone,
    req.detectedPhone,
  ].some(v => v && v.toLowerCase().includes(q));
};

const groupByPhone = (requests) => {
  const groups = new Map();

  for (const req of requests) {
    const rawPhone = req.clientPhone || req.detectedPhone || '';
    const key = normalizePhoneKey(rawPhone);

    if (!key) {
      groups.set(`__nophone_${req.id}`, {
        phone: '',
        name: '',
        requests: [req],
      });
    } else {
      if (!groups.has(key)) {
        groups.set(key, {
          phone: rawPhone,
          name: req.clientName || req.detectedName || '',
          requests: [],
        });
      }
      const g = groups.get(key);
      g.requests.push(req);
      if (!g.name && (req.clientName || req.detectedName)) {
        g.name = req.clientName || req.detectedName || '';
      }
      if (!g.phone && rawPhone) g.phone = rawPhone;
    }
  }

  return Array.from(groups.values());
};

const STATUS_STYLES = {
  CONVERTED: 'text-green-400 bg-green-400/10',
  DISMISSED: 'text-zinc-500 bg-zinc-500/10',
};

const RequestCard = ({ request, onUpdateStatus, locale, t, isProcessed = false }) => {
  const name = request.clientName || request.detectedName || '';
  const phone = request.clientPhone || request.detectedPhone || '';
  const text = request.reason || request.rawText || '';

  if (isProcessed) {
    return (
      <div
        className="p-3 bg-zinc-900/50 border border-zinc-900 rounded-sm opacity-60"
        data-testid={`request-processed-${request.id}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{text}</p>
            {(name || phone) && (
              <div className="flex gap-3 mt-1">
                {name && <span className="text-xs text-zinc-500">{name}</span>}
                {phone && <span className="text-xs text-zinc-500">{phone}</span>}
              </div>
            )}
          </div>
          <span className={`px-2 py-1 rounded text-xs flex-shrink-0 ${STATUS_STYLES[request.status] || 'text-zinc-500 bg-zinc-500/10'}`}>
            {t(`requests.status.${request.status.toLowerCase()}`)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-4 bg-card border border-zinc-800 rounded-sm"
      data-testid={`request-${request.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
          <MessageSquare className="w-5 h-5 text-zinc-400" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm whitespace-pre-wrap">{text}</p>

          <div className="flex flex-wrap gap-2 mt-3">
            {name && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-xs">
                <User className="w-3 h-3" />
                {name}
              </span>
            )}
            {phone && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-xs">
                <Phone className="w-3 h-3" />
                {phone}
              </span>
            )}
            {request.detectedDateTime && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-xs">
                <Calendar className="w-3 h-3" />
                {request.detectedDateTime}
              </span>
            )}
          </div>

          <div className="text-xs text-zinc-600 mt-2">
            {format(parseISO(request.createdAt), 'dd/MM/yyyy HH:mm', { locale })}
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="border-green-600 text-green-400 hover:bg-green-600/10"
            onClick={() => onUpdateStatus(request.id, 'CONVERTED')}
            data-testid={`convert-request-${request.id}`}
          >
            <Check className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-500 hover:bg-zinc-800"
            onClick={() => onUpdateStatus(request.id, 'DISMISSED')}
            data-testid={`dismiss-request-${request.id}`}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const PhoneGroup = ({ group, onUpdateStatus, locale, t, isProcessed = false }) => {
  const [open, setOpen] = useState(true);
  const isMultiple = group.requests.length > 1;
  const label = group.name || group.phone || '—';

  if (!isMultiple) {
    return (
      <RequestCard
        request={group.requests[0]}
        onUpdateStatus={onUpdateStatus}
        locale={locale}
        t={t}
        isProcessed={isProcessed}
      />
    );
  }

  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 bg-zinc-900/60 hover:bg-zinc-800/60 transition-colors text-left"
        onClick={() => setOpen(o => !o)}
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          : <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />}
        <Phone className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
        <span className="text-sm font-medium truncate">{label}</span>
        {group.phone && group.name && (
          <span className="text-xs text-zinc-500 truncate hidden sm:inline">{group.phone}</span>
        )}
        <span className="ml-auto text-xs text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded flex-shrink-0">
          {group.requests.length}
        </span>
      </button>
      {open && (
        <div className="divide-y divide-zinc-800/50">
          {group.requests.map(req => (
            <div key={req.id} className="bg-card">
              <RequestCard
                request={req}
                onUpdateStatus={onUpdateStatus}
                locale={locale}
                t={t}
                isProcessed={isProcessed}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Requests = () => {
  const { api, language, user } = useAuth();
  const { t } = useTranslation();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    rawText: '',
    detectedName: '',
    detectedPhone: '',
    detectedService: '',
    detectedDateTime: '',
  });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/requests?limit=200&skip=0');
      setRequests(res.data);
      if (res.data.length >= 200) {
        toast.warning('Mostrando los últimos 200 registros');
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error(t('requests.toast.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    setSearch('');
  }, [activeTab]);

  const handleCreate = async () => {
    if (!formData.rawText.trim()) {
      toast.error(t('requests.toast.textRequired'));
      return;
    }
    setFormLoading(true);
    try {
      await api.post('/requests', {
        ...formData,
        centerId: user.centerId || '',
        status: 'PENDING',
      });
      toast.success(t('requests.toast.created'));
      setShowDialog(false);
      setFormData({ rawText: '', detectedName: '', detectedPhone: '', detectedService: '', detectedDateTime: '' });
      fetchRequests();
    } catch (error) {
      toast.error(t('requests.toast.createError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, status) => {
    try {
      const request = requests.find(r => r.id === requestId);
      await api.put(`/requests/${requestId}`, { ...request, status });
      toast.success(t('requests.toast.updated'));
      fetchRequests();
    } catch (error) {
      toast.error(t('requests.toast.updateError'));
    }
  };

  const allPending = requests.filter(r => r.status === 'PENDING');
  const allProcessed = requests.filter(r => r.status === 'CONVERTED' || r.status === 'DISMISSED');

  const filteredPending = useMemo(
    () => allPending.filter(r => matchesSearch(r, search)),
    [allPending, search],
  );
  const filteredProcessed = useMemo(
    () => allProcessed.filter(r => matchesSearch(r, search)),
    [allProcessed, search],
  );

  const pendingGroups = useMemo(() => groupByPhone(filteredPending), [filteredPending]);
  const processedGroups = useMemo(() => groupByPhone(filteredProcessed), [filteredProcessed]);

  const activeGroups = activeTab === 'pending' ? pendingGroups : processedGroups;
  const activeFiltered = activeTab === 'pending' ? filteredPending : filteredProcessed;

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="requests-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
            {t('requests.title')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {allPending.length} {t('requests.status.pending').toLowerCase()}
          </p>
        </div>

        <Button
          className="btn-primary"
          onClick={() => setShowDialog(true)}
          data-testid="add-request-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('requests.addRequest')}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-zinc-800">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'pending'
              ? 'border-white text-white'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
          onClick={() => setActiveTab('pending')}
          data-testid="tab-pending"
        >
          {t('requests.status.pending')}
          {allPending.length > 0 && (
            <span className="ml-2 text-xs bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
              {allPending.length}
            </span>
          )}
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'processed'
              ? 'border-white text-white'
              : 'border-transparent text-zinc-500 hover:text-zinc-300'
          }`}
          onClick={() => setActiveTab('processed')}
          data-testid="tab-processed"
        >
          {t('requests.processed')}
          {allProcessed.length > 0 && (
            <span className="ml-2 text-xs bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">
              {allProcessed.length}
            </span>
          )}
        </button>
      </div>

      {/* Search bar */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`${t('requests.fields.name')} / ${t('requests.fields.phone')}…`}
          className="input-tactical pl-9"
          data-testid="requests-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : activeFiltered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {search ? t('requests.noResults') || 'Sin resultados' : t('requests.noRequests')}
        </div>
      ) : (
        <div className="grid gap-3">
          {activeGroups.map((group, i) => (
            <PhoneGroup
              key={group.phone ? normalizePhoneKey(group.phone) : `nophone-${i}`}
              group={group}
              onUpdateStatus={handleUpdateStatus}
              locale={locale}
              t={t}
              isProcessed={activeTab === 'processed'}
            />
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {t('requests.addRequest')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('requests.rawText')} *</Label>
              <Textarea
                value={formData.rawText}
                onChange={(e) => setFormData(f => ({ ...f, rawText: e.target.value }))}
                placeholder="Paste call notes or WhatsApp message..."
                className="input-tactical"
                rows={4}
                data-testid="request-text-input"
              />
            </div>

            <div className="p-3 bg-zinc-900 rounded-sm">
              <div className="text-xs text-zinc-500 uppercase tracking-wide mb-3">
                {t('requests.detectedInfo')}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('requests.fields.name')}</Label>
                  <Input
                    value={formData.detectedName}
                    onChange={(e) => setFormData(f => ({ ...f, detectedName: e.target.value }))}
                    className="input-tactical h-8 text-sm"
                    data-testid="detected-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('requests.fields.phone')}</Label>
                  <Input
                    value={formData.detectedPhone}
                    onChange={(e) => setFormData(f => ({ ...f, detectedPhone: e.target.value }))}
                    className="input-tactical h-8 text-sm"
                    data-testid="detected-phone-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('requests.fields.service')}</Label>
                  <Input
                    value={formData.detectedService}
                    onChange={(e) => setFormData(f => ({ ...f, detectedService: e.target.value }))}
                    className="input-tactical h-8 text-sm"
                    data-testid="detected-service-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('requests.fields.dateTime')}</Label>
                  <Input
                    value={formData.detectedDateTime}
                    onChange={(e) => setFormData(f => ({ ...f, detectedDateTime: e.target.value }))}
                    className="input-tactical h-8 text-sm"
                    data-testid="detected-datetime-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleCreate}
                disabled={formLoading}
                data-testid="save-request-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
